import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Hamburger from '~/react/shared/Hamburger';
import AmpHamburger from '~/react/Amp/Hamburger';
import { setLinkCetDataVars } from '~/server/utils/cetTrackingHelper';
import AmpMemberStatus from '~/react/Amp/MemberStatus';
import LogoComponent from '~/public/assets/icons/new-logo.svg';
import SmallLogoComponent from '~/public/assets/icons/new-h-logo.svg';
import BackToHome from '~/react/Entry/BackToHome';
import NotificationCenter from './NotificationCenter';
import MemberStatus from './MemberStatus';

const { SIGN_IN_URL } = require('~/constants');

const NAV_MOBILE_BP = 1024;

// eslint-disable-next-line react/prefer-stateless-function
class Nav extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isSticking: false,
      isMobile: props.isMobile,
      maybeShowBackToHome: false,
    };
    // this.ref = React.createRef();
    this.isEntry = this.props.isEntry;
    [
      'handleResize',
      'testPagePosition',
    ].forEach((key) => { this[key] = this[key].bind(this); });
  }

  componentDidMount() {
    if (this.isEntry) {
      const observerSpan = document.getElementById('percent-trigger');
      if (typeof IntersectionObserver === 'function' && observerSpan) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              if (!this.state.maybeShowBackToHome) {
                this.setState({ maybeShowBackToHome: true });
              }
            }
          });
        }, { threshold: 1 });
        this.intersectionObserver.observe(observerSpan);
      }
    }
    //
    this.testPagePosition();
    document.addEventListener('scroll', this.testPagePosition);
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    if (this.isEntry) {
      this.intersectionObserver.unobserve(document.getElementById('percent-trigger'));
    }
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('scroll', this.testPagePosition);
  }

  handleResize() {
    if (window.innerWidth < NAV_MOBILE_BP) {
      this.setState({ isMobile: true }, this.testPagePosition);
    } else {
      this.setState({ isMobile: false }, () => {
        this.testPagePosition();
      });
    }
  }

  testPagePosition() {
    const { scrollY, innerWidth } = window;

    const siteNav = document.querySelector('.desktop-sticky-nav');
    const siteNavHeight = siteNav ? siteNav.offsetHeight : 0;
    const masthead = document.querySelector('.masthead');
    const mastheadHeight = masthead ? masthead.offsetHeight : 0;
    const navStickingDepth = mastheadHeight + siteNavHeight;
    const shouldStick = scrollY >= navStickingDepth;

    const isCurrentlyMobile = innerWidth < NAV_MOBILE_BP;
    // set state only if the values have changed to avoid re rendering
    if (this.state.isMobile !== isCurrentlyMobile || this.state.isSticking !== shouldStick) {
      this.setState({
        isMobile: isCurrentlyMobile,
        isSticking: shouldStick,
      });
    }
  }

  render() {
    const {
      theme,
      editionInfo,
      type,
      isAmp,
      signInUrl,
      profileUrl,
      subscribeUrl,
      supportHuffpostUrl,
      hideSupportUsButton,
      hideNotificationCenter,
      signInTrackingParams,
      isEntry,
      title,
    } = this.props;

    const editionLink = editionInfo.link;

    const logoCetData = setLinkCetDataVars('nav_bar', editionLink, 'huffpost_logo', 'button', 'site_header');

    const {
      isSticking, isMobile, maybeShowBackToHome,
    } = this.state;

    const navClassNames = {
      nav: true,
      'js-cet-subunit': true,
      sticking: isSticking,
      'nav--entry': isEntry,
    };
    navClassNames[`nav--${theme}`] = true;
    navClassNames[`nav--${type}`] = true;

    const memberProps = {
      type,
      signInUrl,
      profileUrl,
      subscribeUrl,
      supportHuffpostUrl,
      hideSupportUsButton,
      signInTrackingParams,
      isMobile: this.props.isMobile,
    };

    const backToHomeButtonCetDataVars = setLinkCetDataVars('nav_bar', editionLink, 'GO TO HUFFPOST', 'button', 'site_header');

    const showNotificationCenter = !isAmp && !hideNotificationCenter && !(isSticking && (isMobile || isEntry));

    const showBigLogo = isAmp || (isSticking && !isEntry) || (isSticking && isEntry && isMobile);
    const showEntryNav = !isAmp && !isMobile && isEntry && isSticking;

    return (
      <nav
        className={classnames(navClassNames)}
      >
        <header className="nav__header">
          <div className="nav__content">
            <div className="nav__left">
              {isAmp ? <AmpHamburger /> : <Hamburger /> }
              {showNotificationCenter && <NotificationCenter editionInfo={editionInfo} />}
              {showEntryNav && (
                <div className="nav__entry">
                    {maybeShowBackToHome ? (
                      <div className="nav__entry__back-to-home fade-in">
                        <BackToHome homeUrl={editionLink} options={{ cetDataVars: backToHomeButtonCetDataVars }} modifiers={['nav']} />
                      </div>
                    ) : (
                      <>
                        <a
                          className="nav__logo__link nav__logo__link--small fade-in"
                          href="/"
                          {...logoCetData}
                        >
                          <SmallLogoComponent />
                        </a>
                        <div className="nav__entry__title fade-in">
                          {title}
                        </div>
                      </>
                    )}
                </div>
              )}
              {showBigLogo && (
                <a
                  className="nav__logo__link nav__logo__link--big fade-in"
                  href="/"
                  {...logoCetData}
                >
                  <LogoComponent />
                </a>
              )}
            </div>
            <div className="nav__right__container">
              {editionInfo.membershipEnabled && (isAmp ? <AmpMemberStatus {...memberProps} /> : <MemberStatus {...memberProps} />)}
            </div>
          </div>
        </header>
      </nav>
    );
  }
}

Nav.propTypes = {
  theme: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  isAmp: PropTypes.bool,
  signInUrl: PropTypes.string,
  profileUrl: PropTypes.string,
  subscribeUrl: PropTypes.string,
  isMobile: PropTypes.bool,
  editionInfo: PropTypes.shape({}).isRequired,
  supportHuffpostUrl: PropTypes.string,
  hideSupportUsButton: PropTypes.bool,
  hideNotificationCenter: PropTypes.bool,
  signInTrackingParams: PropTypes.arrayOf(PropTypes.string),
};

Nav.defaultProps = {
  isAmp: false,
  signInUrl: SIGN_IN_URL,
  profileUrl: '/member',
  subscribeUrl: '/subscribe?ncid=huffpost_globaldesk_ct6htebthyu',
  isMobile: false,
  supportHuffpostUrl: null,
  hideSupportUsButton: false,
  hideNotificationCenter: false,
  signInTrackingParams: [],
};

export default Nav;
